import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import { eventsApi, type EventSummary } from '../services/api'
import { formatEventDate, formatEventRange } from '../lib/datetime'
import '../styles/blog.css'
import '../styles/events.css'

const PAGE_TITLE = 'OpenDeploy Events'
const PAGE_DESCRIPTION =
  'Meetups, demos, and build nights from the OpenDeploy community. Join an event, submit your work, and vote for your favorites.'

const DISCORD_URL = import.meta.env.VITE_DISCORD_URL || 'https://discord.gg/R4udP6TbM2'

function formatRange(startIso?: string, endIso?: string, tz?: string) {
  return formatEventRange(startIso, endIso, tz, { year: 'numeric', month: 'long', day: 'numeric' })
}

function shortDate(iso?: string, tz?: string) {
  return formatEventDate(iso, tz)
}

// An event has passed once its end (or its start, when there is no end) is in the
// past. Undated events are never "past". Used to keep the featured banner on the
// soonest upcoming event while past events still appear in the grid below.
function isEventPast(event: EventSummary): boolean {
  const end = event.ends_at ?? event.starts_at
  return end ? new Date(end).getTime() < Date.now() : false
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M7 3v3M17 3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth={1.7} />
    </svg>
  )
}

// Frosted-glass hero for the soonest upcoming event: the cover image fills the
// whole banner and a single blurred glass layer sits over all of it, so the
// surface reads as one cohesive panel rather than a split.
function FeaturedEvent({ event }: { event: EventSummary }) {
  return (
    <Link
      to={`/events/${event.slug}`}
      className={`od-event-banner${event.cover_image_url ? '' : ' is-plain'}`}
    >
      {event.cover_image_url && (
        <img
          className="od-event-banner-img"
          src={event.cover_image_url}
          alt=""
          loading="eager"
        />
      )}
      <div className="od-event-banner-glass" aria-hidden="true" />
      <div className="od-event-banner-text">
        <span className="od-event-banner-tag">Next event</span>
        <h2>{event.name}</h2>
        <div className="od-event-banner-meta">
          {event.starts_at && (
            <span className="od-event-banner-row">
              <CalendarIcon />
              {formatRange(event.starts_at, event.ends_at, event.timezone)}
            </span>
          )}
          {event.location && (
            <span className="od-event-banner-row">
              <PinIcon />
              {event.location}
            </span>
          )}
        </div>
        <span className="od-event-banner-cta">
          View event
          <ArrowIcon />
        </span>
      </div>
    </Link>
  )
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M5 12h14M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// Grid card (thumbnail + title + date) for the list below the featured event.
function EventCard({ event }: { event: EventSummary }) {
  return (
    <Link to={`/events/${event.slug}`} className="od-event-card">
      <div className="od-event-card-cover">
        {event.cover_image_url ? (
          <img src={event.cover_image_url} alt="" loading="lazy" />
        ) : (
          <div className="od-event-card-cover-fallback" aria-hidden="true">
            {event.name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <h3>{event.name}</h3>
      <div className="od-event-card-meta">
        {event.starts_at && (
          <span className="od-event-card-date">{shortDate(event.starts_at, event.timezone)}</span>
        )}
        {event.location && <span className="od-event-card-loc">{event.location}</span>}
      </div>
    </Link>
  )
}

export default function Events() {
  const [events, setEvents] = useState<EventSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    eventsApi
      .listEvents()
      .then((res) => {
        if (active) setEvents(res.data || [])
      })
      .catch(() => {
        if (active) setError('Could not load events. Please try again later.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  // The banner leads with the soonest upcoming event (backend returns
  // upcoming-first); past events still appear in the grid below but never lead.
  const featured = events.find((event) => event.starts_at && !isEventPast(event))

  return (
    <>
      <SEO title={PAGE_TITLE} description={PAGE_DESCRIPTION} path="/events" />

      <div className="od-event-page od-blog-light od-event-light">
        <SiteNav />
        <div className="od-event-frame">
          <section className="od-event-listing" aria-label="Events">
            {loading ? (
              <p className="od-event-state">Loading events…</p>
            ) : error ? (
              <p className="od-event-state">{error}</p>
            ) : events.length === 0 ? (
              <p className="od-event-state">No events yet — check back soon.</p>
            ) : (
              <>
                {featured && <FeaturedEvent event={featured} />}
                <h2 className="od-event-list-heading">All events</h2>
                <div className="od-event-grid">
                  {events.map((event) => (
                    <EventCard key={event.slug} event={event} />
                  ))}
                </div>
              </>
            )}

            <aside className="od-event-host">
              <div className="od-event-host-body">
                <h2>Want to host an event?</h2>
                <p>
                  Bring OpenDeploy to your city — run a build night, a workshop, or a demo
                  evening. We'll help with the playbook, swag, and credits.
                </p>
              </div>
              <a
                className="od-event-host-cta"
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
              >
                Get in touch
                <ArrowIcon />
              </a>
            </aside>
          </section>
        </div>
        <Footer />
      </div>
    </>
  )
}
